import { useMutation, useQueryClient } from '@tanstack/react-query'
import { RotateCcw } from 'lucide-react'
import { useMemo, useState, type ReactNode, type SyntheticEvent } from 'react'

import type { FinanceEntryDetail, PaymentMethodSummary } from '@aeris/contracts'
import { checkSettlement, parseAmount, ZERO_AMOUNT } from '@aeris/finance'
import { add, formatMoney, isPositive, subtract, toString } from '@aeris/money'
import type { Decimal } from '@aeris/money'
import {
  Badge,
  Button,
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogFooter,
  Field,
  MoneyInput,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
  Textarea,
  notify,
} from '@aeris/ui'

/** Uma linha da tabela de baixas — o tipo que `FinanceEntryDetail` já carrega. */
type SettlementRow = FinanceEntryDetail['settlements'][number]

import {
  ApiError,
  reverseFinanceSettlement,
  settleFinanceEntry,
} from '../lib/api.ts'
import { formatDate } from '../lib/date-format.ts'
import { parseTypedMoney, toWireAmount } from '../lib/price-format.ts'

/**
 * A baixa.
 *
 * The four amounts are four fields because they are four different facts. What
 * came off the debt, what lateness cost (juros and multa), and what a
 * negotiation saved (desconto) are separately reportable, and a single "valor
 * pago" box would collapse them into a number nobody can take apart afterwards.
 *
 * The rules are `@aeris/finance` — the same functions the server runs — so the
 * dialog can say "faltam R$ 250,00" while somebody types, and the answer it
 * gives is the answer the request will get.
 *
 * ## Mais de uma forma numa sentada (F107)
 *
 * *"aqui tem que ter múltiplas formas"* — o motoboy volta e diz "vinte no
 * dinheiro, vinte e cinco no PIX".
 *
 * Isto **não** muda o modelo: a F034 já tinha decidido que R$ 50,00 no cartão e
 * R$ 50,00 em dinheiro são *"duas baixas parciais do mesmo título, cada uma com
 * a sua forma de pagamento"*. Continuam sendo N baixas; o que muda é que o
 * operador as informa de uma vez em vez de fechar e reabrir o diálogo.
 *
 * Por isso não há contrato novo nem rota nova. O laço é do cliente, e o que o
 * faz funcionar é encadear o `version`: o gatilho `touch_row` incrementa a
 * versão a cada baixa, então a resposta de uma é o que autoriza a seguinte.
 */

/**
 * Uma forma informada, esperando virar baixa (F107).
 *
 * `key` de módulo e não índice: remover a primeira de três não pode fazer as
 * outras duas trocarem de identidade para o React.
 */
type Tender = {
  readonly key: string
  readonly methodId: string
  readonly methodName: string
  readonly amount: Decimal
}

let sequence = 0

/**
 * Parou no meio, e diz onde (F107).
 *
 * Carrega quantas formas entraram antes da recusa, porque é essa a frase que o
 * operador precisa ler: *"1 de 2 formas foram registradas"* mais o motivo. Um
 * "não foi possível registrar a baixa" seco, depois de metade do dinheiro ter
 * entrado, é a pior mensagem possível.
 */
class PartialSettlement extends Error {
  readonly done: number
  readonly total: number

  constructor(done: number, total: number, cause: unknown) {
    super('baixa parcial', { cause })
    this.name = 'PartialSettlement'
    this.done = done
    this.total = total
  }
}

export type FinanceSettleDialogProps = {
  entry: FinanceEntryDetail
  methods: readonly PaymentMethodSummary[]
  /**
   * A gaveta onde o dinheiro em espécie entra (ADR-0021, decisão 9).
   *
   * Só o dinheiro precisa dela: um cartão não entra em caixa nenhum. Nula
   * significa que não há turno aberto neste terminal, e a tela diz isso antes
   * de alguém digitar o valor — não depois de o servidor recusar.
   */
  cashSessionId?: string | null
  /**
   * POR QUE não há gaveta — F099.
   *
   * `cashSessionId: null` significa duas coisas diferentes, e a tela dizia só
   * uma: "não há caixa aberto neste terminal" é verdade quando o turno está
   * fechado, e é inútil quando **este navegador nunca escolheu um terminal** —
   * a pessoa vai procurar um caixa aberto que já está aberto.
   *
   * Só quem chama sabe a diferença, então ela vem de fora em vez de ser
   * deduzida aqui.
   */
  cashUnavailable?: 'no-terminal' | 'no-session'
  onClose: () => void
}

export const FinanceSettleDialog = ({
  entry,
  methods,
  cashSessionId = null,
  cashUnavailable = 'no-session',
  onClose,
}: FinanceSettleDialogProps): ReactNode => {
  const queryClient = useQueryClient()

  const outstanding = parseAmount(entry.outstanding)

  /**
   * As formas já informadas, fora a que está no formulário (F107).
   *
   * Uma lista, e não um campo a mais, porque cada linha vira uma baixa própria
   * — é o modelo da F034, não um contorno dele. O par valor/forma do formulário
   * é a ÚLTIMA linha e não precisa ser "adicionado": quem recebe numa forma só,
   * o caso esmagadoramente comum, continua preenchendo dois campos e enviando.
   */
  const [tenders, setTenders] = useState<Tender[]>([])

  // Pre-filled with what is owed, unlike the purchase receipt: paying a título
  // in full is the ordinary case and the number is not in dispute — it is on
  // the boleto. A receipt is different because there somebody has to count.
  // Seeded in wire form, not display form: MoneyInput speaks the same
  // fixed-point string the API does, so what the field holds is what gets
  // sent. Handing it "R$ 450,00" would make it read the symbol as digits.
  const [amount, setAmount] = useState(() => toString(outstanding))
  const [interest, setInterest] = useState('')
  const [fine, setFine] = useState('')
  const [discount, setDiscount] = useState('')
  const [methodId, setMethodId] = useState(
    () => entry.expectedMethodId ?? methods[0]?.id ?? ''
  )
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)

  const typed = useMemo(() => {
    const read = (value: string) =>
      value.trim().length === 0 ? ZERO_AMOUNT : (parseTypedMoney(value) ?? null)

    const principal = read(amount)
    const juros = read(interest)
    const multa = read(fine)
    const desconto = read(discount)

    if (
      principal === null ||
      juros === null ||
      multa === null ||
      desconto === null
    ) {
      return null
    }

    return {
      amount: principal,
      interestAmount: juros,
      fineAmount: multa,
      discountAmount: desconto,
    }
  }, [amount, interest, fine, discount])

  /** O que as formas já informadas somam. */
  const tendered = useMemo(
    () =>
      tenders.reduce<Decimal>(
        (running, tender) => add(running, tender.amount),
        ZERO_AMOUNT
      ),
    [tenders]
  )

  /**
   * Com mais de uma forma, os três ajustes ficam de fora (F107).
   *
   * Não há resposta não-arbitrária para "de qual linha sai o juro": os valores
   * informados são DINHEIRO, e o principal é o que abate a dívida. Ratear o
   * ajuste entre as linhas inventaria uma alocação que ninguém pediu e que
   * ninguém conseguiria auditar depois.
   *
   * A saída continua existindo e é a de hoje: duas baixas separadas, cada uma
   * com o seu ajuste. É limitação declarada, não esquecimento.
   */
  const splitting = tenders.length > 0

  /**
   * The same check the server will run, against the balance the screen holds.
   *
   * It can disagree with the server if somebody else settles in the meantime —
   * which is exactly what the version token is for. This is here to answer
   * while typing, not to be the authority.
   *
   * `settledAmount` soma o que as outras formas já vão abater (F107): sem isso
   * a tela deixaria informar R$ 45,00 em dinheiro mais R$ 45,00 em PIX num
   * título de R$ 45,00, e a recusa só apareceria no meio do laço, com a
   * primeira baixa já gravada.
   */
  const preview = useMemo(
    () =>
      typed === null
        ? null
        : checkSettlement(
            {
              status: entry.status,
              amount: parseAmount(entry.amount),
              settledAmount: add(parseAmount(entry.settledAmount), tendered),
            },
            typed
          ),
    [typed, tendered, entry.status, entry.amount, entry.settledAmount]
  )

  /**
   * Dinheiro sem gaveta aberta.
   *
   * O servidor recusa de qualquer forma; isto é para que a pessoa saiba antes
   * de preencher quatro campos.
   */
  const isCash = (id: string): boolean =>
    methods.find((method) => method.id === id)?.kind === 'cash'

  // Vale para QUALQUER linha, e não só para a que está no formulário (F107):
  // basta uma delas ser em dinheiro para faltar gaveta.
  const cashWithoutTill =
    cashSessionId === null &&
    (isCash(methodId) || tenders.some((tender) => isCash(tender.methodId)))

  /** O que falta para fechar o título, já descontadas as formas informadas. */
  const remaining = subtract(outstanding, tendered)

  const addTender = (): void => {
    const method = methods.find((entry_) => entry_.id === methodId)
    if (method === undefined || typed === null || !isPositive(typed.amount)) {
      return
    }

    sequence += 1
    setTenders((current) => [
      ...current,
      {
        key: `tender-${String(sequence)}`,
        methodId: method.id,
        methodName: method.name,
        amount: typed.amount,
      },
    ])

    // O campo passa a seguir o que ainda falta, como no balcão: quem informa a
    // segunda forma quase sempre informa exatamente o resto.
    const rest = subtract(remaining, typed.amount)
    setAmount(isPositive(rest) ? toString(rest) : '')

    /*
     * Os ajustes são zerados, e não só desabilitados.
     *
     * Deixá-los mostrando o que alguém digitou enquanto o envio manda zero
     * seria a tela dizendo um número e o registro guardando outro — o defeito
     * que a F100 custou caro para achar. Cinza e com valor dentro é pior que
     * vazio: parece que vai ser cobrado.
     */
    setInterest('')
    setFine('')
    setDiscount('')
    setError(null)
  }

  const settle = useMutation({
    mutationFn: async () => {
      if (typed === null) throw new Error('valor inválido')

      // O par do formulário é a última linha. Uma forma só continua sendo uma
      // requisição só.
      const all: readonly Tender[] = [
        ...tenders,
        {
          key: 'form',
          methodId,
          methodName: '',
          amount: typed.amount,
        },
      ]

      const single = all.length === 1
      let version = entry.version
      let done = 0

      for (const tender of all) {
        try {
          const response = await settleFinanceEntry(entry.id, {
            version,
            branchId: entry.branchId,
            paymentMethodId: tender.methodId,
            amount: toWireAmount(tender.amount),
            // Os ajustes só existem quando há uma forma só — ver `splitting`.
            interestAmount: toWireAmount(
              single ? typed.interestAmount : ZERO_AMOUNT
            ),
            fineAmount: toWireAmount(single ? typed.fineAmount : ZERO_AMOUNT),
            discountAmount: toWireAmount(
              single ? typed.discountAmount : ZERO_AMOUNT
            ),
            cashSessionId,
            note: note.trim().length === 0 ? null : note.trim(),
          })

          // O que faz o laço funcionar: `touch_row` incrementa a versão a cada
          // baixa, então a resposta de uma autoriza a seguinte.
          version = response.entry.version
          done += 1
        } catch (cause) {
          /*
           * Para aqui, e NÃO desfaz o que já entrou.
           *
           * Se a primeira forma era dinheiro, aquele dinheiro está fisicamente
           * na gaveta — estornar faria o livro mentir sobre uma cédula que
           * está lá. Uma baixa parcial é estado legal do modelo, aparece na
           * tabela acima, e o operador vê quanto ainda falta.
           */
          throw new PartialSettlement(done, all.length, cause)
        }
      }

      return { settled: done }
    },
    onSuccess: () => {
      // O retorno à vista (F248): a baixa fechava o diálogo calada.
      notify.success(`Baixa registrada: ${entry.description}.`)
      // Closed on the server's acceptance, not on a list finishing its
      // refetch — see product-form.tsx for the failure that pattern produced.
      onClose()
      void queryClient.invalidateQueries({ queryKey: ['finance'] })
    },
    onError: (cause: unknown) => {
      const failure = cause instanceof PartialSettlement ? cause : null
      const reason =
        (failure?.cause ?? cause) instanceof ApiError
          ? ((failure?.cause ?? cause) as ApiError).message
          : 'Não foi possível registrar a baixa.'

      if (failure !== null && failure.done > 0) {
        // As linhas gravadas foram consumidas: mantê-las na tela convidaria a
        // pessoa a enviá-las duas vezes.
        setTenders([])
        setAmount('')
        setError(
          `${String(failure.done)} de ${String(failure.total)} formas foram registradas. ${reason}`
        )
        void queryClient.invalidateQueries({ queryKey: ['finance'] })
        return
      }

      setError(reason)
    },
  })

  /*
   * Estornar é mexer em dinheiro, e mexia com UM clique (F069).
   *
   * O botão ficava numa linha de tabela, do tamanho de um "Editar", e
   * disparava a operação direto. Um estorno reabre um título que a loja já
   * considerava pago e desfaz um lançamento no caixa — `docs/UI_UX.md` só
   * dispensa confirmação para operação inofensiva, e esta é o contrário disso.
   *
   * A frase do diálogo carrega o VALOR, porque "Tem certeza?" não ajuda quem
   * tem três baixas na tela e precisa saber qual delas está prestes a sumir.
   */
  const [reverting, setReverting] = useState<SettlementRow | null>(null)

  const reverse = useMutation({
    mutationFn: async (settlementId: string) =>
      await reverseFinanceSettlement(entry.id, settlementId, {
        version: entry.version,
        note: null,
      }),
    onSuccess: () => {
      notify.success(`Baixa estornada: ${entry.description}.`)
      // Closed on the server's acceptance, not on a list finishing its
      // refetch — see product-form.tsx for the failure that pattern produced.
      onClose()
      void queryClient.invalidateQueries({ queryKey: ['finance'] })
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível estornar a baixa.'
      )
    },
  })

  const submit = (event: SyntheticEvent): void => {
    event.preventDefault()
    setError(null)
    settle.mutate()
  }

  const settled = entry.status === 'settled'

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent
        title={entry.description}
        description={`${entry.partyName} · vence ${formatDate(entry.dueAt)}`}
        className="max-w-2xl"
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap gap-4 rounded-(--radius-md) border border-(--border-subtle) px-3 py-2 text-(length:--text-sm)">
            <span className="text-(--text-secondary)">
              Valor{' '}
              <strong className="font-(--font-mono) text-(--text-primary)">
                {formatMoney(parseAmount(entry.amount))}
              </strong>
            </span>
            <span className="text-(--text-secondary)">
              Pago{' '}
              <strong className="font-(--font-mono) text-(--text-primary)">
                {formatMoney(parseAmount(entry.settledAmount))}
              </strong>
            </span>
            <span className="text-(--text-secondary)">
              Em aberto{' '}
              <strong className="font-(--font-mono) text-(--text-primary)">
                {formatMoney(outstanding)}
              </strong>
            </span>
            {entry.isOverdue ? (
              <Badge tone="danger">
                {entry.daysOverdue === 1
                  ? '1 dia de atraso'
                  : `${String(entry.daysOverdue)} dias de atraso`}
              </Badge>
            ) : null}
          </div>

          {/*
            As baixas JÁ FEITAS vêm antes do formulário, e não depois do
            rodapé (F069).

            Estavam abaixo do `</form>`, então os botões "Fechar" e
            "Registrar baixa" apareciam no MEIO do diálogo, com uma tabela de
            dinheiro — e botões de estorno — pendurada embaixo deles. É o
            mesmo defeito que a F058 corrigiu no diálogo de filas, aqui numa
            tela que mexe em dinheiro.

            A ordem agora é a dos fatos: o que já foi pago, e depois o que se
            está prestes a pagar. O rodapé fecha o diálogo, que é o único
            lugar onde um rodapé faz sentido.
          */}
          {entry.settlements.length === 0 ? null : (
            <div className="flex flex-col gap-2">
              <h3 className="text-(length:--text-sm) font-(--weight-semibold) text-(--text-primary)">
                Baixas
              </h3>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>Data</TableHeaderCell>
                      <TableHeaderCell>Forma</TableHeaderCell>
                      <TableHeaderCell className="text-right">
                        Valor
                      </TableHeaderCell>
                      <TableHeaderCell className="text-right">
                        Pago
                      </TableHeaderCell>
                      <TableHeaderCell className="text-right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {entry.settlements.map((settlement) => (
                      <TableRow key={settlement.id}>
                        <TableCell className="whitespace-nowrap">
                          {formatDate(settlement.settledOn)}
                        </TableCell>
                        <TableCell>
                          {settlement.reversesSettlementId === null ? (
                            (settlement.paymentMethodName ?? '—')
                          ) : (
                            // The reversal is a row of its own, visible beside
                            // what it undid. The trail shows both events, never
                            // the second one only.
                            <Badge tone="warning">Estorno</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-(--font-mono)">
                          {formatMoney(parseAmount(settlement.amount))}
                        </TableCell>
                        <TableCell className="text-right font-(--font-mono)">
                          {formatMoney(parseAmount(settlement.paidAmount))}
                        </TableCell>
                        <TableCell className="text-right">
                          {settlement.reversesSettlementId === null &&
                          !settlement.isReversed ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={reverse.isPending}
                              leadingIcon={
                                <RotateCcw
                                  aria-hidden="true"
                                  className="h-3.5 w-3.5"
                                />
                              }
                              onClick={() => {
                                setReverting(settlement)
                              }}
                            >
                              Estornar
                            </Button>
                          ) : settlement.isReversed ? (
                            <span className="text-(length:--text-xs) text-(--text-muted)">
                              estornada
                            </span>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          )}

          {settled ? null : (
            <form onSubmit={submit} className="flex flex-col gap-3">
              {/*
                As formas já informadas (F107). Cada uma vira uma baixa própria
                ao enviar — é o modelo da F034, mostrado antes de acontecer.
              */}
              {splitting && (
                <ul className="flex flex-col gap-1">
                  {tenders.map((tender) => (
                    <li
                      key={tender.key}
                      className="flex items-center justify-between gap-3 rounded-(--radius-md) border border-(--border-subtle) bg-(--surface-sunken) px-3 py-2"
                    >
                      <span className="text-(length:--text-sm) text-(--text-primary)">
                        {tender.methodName}
                      </span>
                      <span className="flex items-center gap-3">
                        <span className="font-(--font-mono) text-(length:--text-sm)">
                          {formatMoney(tender.amount)}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          aria-label={`Remover ${tender.methodName}`}
                          onClick={() => {
                            setTenders((current) =>
                              current.filter((row) => row.key !== tender.key)
                            )
                          }}
                        >
                          Remover
                        </Button>
                      </span>
                    </li>
                  ))}
                </ul>
              )}

              <div className="flex flex-wrap gap-3">
                <Field label="Valor da baixa" className="w-40">
                  {(field) => (
                    <MoneyInput
                      {...field}
                      value={amount}
                      onValueChange={setAmount}
                    />
                  )}
                </Field>

                {/* Juros and multa are separate from the principal because only
                    the principal comes off the debt. Adding them in is how a
                    shop ends up chasing money it has already collected. */}
                <Field label="Juros" className="w-32">
                  {(field) => (
                    <MoneyInput
                      {...field}
                      value={interest}
                      disabled={splitting}
                      onValueChange={setInterest}
                    />
                  )}
                </Field>

                <Field label="Multa" className="w-32">
                  {(field) => (
                    <MoneyInput
                      {...field}
                      value={fine}
                      disabled={splitting}
                      onValueChange={setFine}
                    />
                  )}
                </Field>

                <Field label="Desconto" className="w-32">
                  {(field) => (
                    <MoneyInput
                      {...field}
                      value={discount}
                      disabled={splitting}
                      onValueChange={setDiscount}
                    />
                  )}
                </Field>
              </div>

              <div className="flex flex-wrap gap-3">
                <Field label="Forma de pagamento" className="w-56">
                  {(field) => (
                    <Select
                      {...field}
                      value={methodId}
                      onChange={(event) => {
                        setMethodId(event.target.value)
                      }}
                    >
                      {methods.map((method) => (
                        <option key={method.id} value={method.id}>
                          {method.name}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>

                {/*
                  Acrescentar guarda a linha atual e limpa o campo para a
                  próxima — o motoboy volta dizendo "vinte no dinheiro, vinte e
                  cinco no PIX", e é nessa ordem que ele fala.
                */}
                <div className="flex items-end">
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={
                      settle.isPending ||
                      typed === null ||
                      !isPositive(typed.amount) ||
                      methodId === '' ||
                      preview?.ok !== true
                    }
                    onClick={addTender}
                  >
                    Outra forma
                  </Button>
                </div>

                <Field label="Observação" className="min-w-64 flex-1">
                  {(field) => (
                    <Textarea
                      {...field}
                      rows={2}
                      value={note}
                      onChange={(event) => {
                        setNote(event.target.value)
                      }}
                    />
                  )}
                </Field>
              </div>

              {splitting && (
                <p className="text-(length:--text-xs) text-(--text-muted)">
                  Juros, multa e desconto valem para uma forma só. Com o
                  recebimento dividido não há como dizer de qual parte eles saem
                  — para cobrar juros, registre as baixas separadamente.
                </p>
              )}

              {preview === null ? (
                <p className="text-(length:--text-xs) text-(--danger)">
                  Informe um valor válido.
                </p>
              ) : preview.ok ? (
                <p className="text-(length:--text-xs) text-(--text-secondary)">
                  Sai do caixa{' '}
                  <strong className="font-(--font-mono)">
                    {formatMoney(preview.check.paidAmount)}
                  </strong>
                  .{' '}
                  {preview.check.closesEntry
                    ? 'Quita o título.'
                    : `Restam ${formatMoney(preview.check.outstandingAfter)}.`}
                </p>
              ) : (
                <p className="text-(length:--text-xs) text-(--danger)">
                  {preview.refusal.reason === 'over-settlement'
                    ? // The number, not "não foi possível": money over the debt
                      // is credit that belongs to somebody.
                      `Este título tem apenas ${formatMoney(preview.refusal.outstanding)} em aberto.`
                    : preview.refusal.reason === 'discount-exceeds-total'
                      ? 'O desconto é maior que o valor da baixa.'
                      : preview.refusal.reason === 'entry-already-settled'
                        ? splitting
                          ? // Dito do jeito certo para quem está no meio do
                            // preenchimento: o título não estava quitado quando
                            // o diálogo abriu — são as formas informadas que já
                            // cobrem tudo. "Já está quitado" mandaria a pessoa
                            // procurar um erro que não existe.
                            'As formas informadas já cobrem o total do título.'
                          : 'Este título já está quitado.'
                        : 'O valor da baixa precisa ser maior que zero.'}
                </p>
              )}

              {/*
                Dito antes de digitar o valor, e não depois de o servidor
                recusar: o dinheiro em espécie entra numa gaveta, e sem turno
                aberto não há gaveta para ele entrar (ADR-0021, decisão 9).
              */}
              {cashWithoutTill && (
                <p className="rounded-(--radius-md) border border-(--warning) bg-(--warning-subtle) px-3 py-2 text-(length:--text-xs) text-(--warning)">
                  {cashUnavailable === 'no-terminal'
                    ? 'Uma baixa em dinheiro entra no caixa, e este computador ainda não disse em qual caixa está. Escolha o caixa nesta tela, ou registre a forma de pagamento que você realmente recebeu.'
                    : 'Uma baixa em dinheiro entra no caixa, e não há caixa aberto neste terminal. Abra o caixa, ou registre a forma de pagamento que você realmente recebeu.'}
                </p>
              )}

              {error === null ? null : (
                <p
                  role="alert"
                  className="rounded-(--radius-md) border border-(--danger) bg-(--danger-subtle) px-3 py-2 text-(length:--text-xs) text-(--danger)"
                >
                  {error}
                </p>
              )}

              <DialogFooter>
                <Button type="button" variant="ghost" onClick={onClose}>
                  Fechar
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={
                    settle.isPending ||
                    preview === null ||
                    !preview.ok ||
                    methodId === '' ||
                    cashWithoutTill
                  }
                >
                  Registrar baixa
                </Button>
              </DialogFooter>
            </form>
          )}
        </div>
      </DialogContent>

      <ConfirmDialog
        open={reverting !== null}
        onOpenChange={(aberto) => {
          if (!aberto) setReverting(null)
        }}
        title="Estornar esta baixa?"
        description={
          reverting === null
            ? ''
            : `A baixa de ${formatMoney(parseAmount(reverting.amount))} de ${formatDate(reverting.settledOn)} volta a ficar em aberto, e o lançamento no caixa é desfeito. O estorno fica registrado ao lado dela — nada é apagado.${
                reverting.raisedEntry !== null &&
                reverting.raisedEntry.status === 'open'
                  ? ` Isso também cancela o título a prazo de ${formatDate(reverting.raisedEntry.dueAt)} (${formatMoney(parseAmount(reverting.raisedEntry.amount))}), que esta baixa abriu.`
                  : ''
              }`
        }
        confirmLabel="Estornar a baixa"
        loading={reverse.isPending}
        onConfirm={() => {
          if (reverting !== null) reverse.mutate(reverting.id)
          setReverting(null)
        }}
      />
    </Dialog>
  )
}
